import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from '@usas/ui-web/wrappers/ReactRouterDom';

import validate from './EditFoundationBoardMemberValidation';

import { navLinks } from '../../UseMemberAdministration';

import useMemberAdministrationFoundationBoardData from '@usas/ui-web/state/memberAdministration/foundationBoard/UseMemberAdministrationFoundationBoardData';
import useEnvironmentVariableData from '@usas/ui-web/state/environmentVariable/UseEnvironmentVariableData';

import useForm from '@usas/ui-web/utils/UseForm';
import Constants from '@usas/ui-web/utils/Constants';
import { formatDate } from '@usas/ui-web/utils/DateFunctions';

const useEditFoundationBoardMember = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { USAS_ORG_UNIT_ID, FOUNDATION_BOARD_OF_DIRECTORS_ROLE_GROUP_ID, environmentVariableState } = useEnvironmentVariableData();
  const { memberAdministrationFoundationBoardState, putFoundationBoardDirector } = useMemberAdministrationFoundationBoardData();
  const { formState, errorState, setFormData, onFormValueChanged, handleSubmit, setErrors, setIsDirty, isSubmitting
  } = useForm(getInitialFormState(), submitFormCallback, validate);
  const [viewState, setViewState] = useState(getInitialViewState);

  const onSaveClicked = () => {
    setIsDirty(true);
    handleSubmit();
  };

  const onBackClicked = () => {
    navigate(navLinks.FOUNDATION_BOARD);
  };

  useEffect(() => {
    if (location.state?.personId && location.state?.personOrgRoleDurationId > 0) {
      const personId = location.state.personId;
      const personOrgRoleDurationId = location.state.personOrgRoleDurationId;

      const targetPerson = memberAdministrationFoundationBoardState.arrayData.find(x => x.personId === personId);

      if (targetPerson && Array.isArray(targetPerson.orgRoles)) {
        const targetRole = targetPerson.orgRoles.find(x => x.personOrgRoleDurationId === personOrgRoleDurationId);

        if (targetRole) {
          setFormData({
            ...formState,
            effectiveDate: targetRole.effectiveDate ? formatDate(targetRole.effectiveDate) : '',
            expirationDate: targetRole.expirationDate ? formatDate(targetRole.expirationDate) : ''
          });

          setViewState({
            ...viewState,
            orgRoleName: targetRole.orgRoleName,
            orgRoleId: targetRole.orgRoleId,
            memberName: `${targetPerson.firstName} ${targetPerson.lastName}`,
            memberId: targetPerson.memberId || '',
            personId,
            personOrgRoleId: targetRole.personOrgRoleId,
            personOrgRoleDurationId
          });
        }
      }
    }
    else {
      onBackClicked();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (memberAdministrationFoundationBoardState.isSaved === true) {
      navigate(navLinks.FOUNDATION_BOARD);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberAdministrationFoundationBoardState.isSaved]);

  function submitFormCallback() {
    const existingPersonOrgRoles = getExistingPersonOrgRoles(viewState.personId);

    if (existingPersonOrgRoles) {
      if (isDurationValid(viewState.personOrgRoleId, viewState.personOrgRoleDurationId, formState.effectiveDate, formState.expirationDate, existingPersonOrgRoles)) {
        putFoundationBoardDirector(USAS_ORG_UNIT_ID, viewState.personId, FOUNDATION_BOARD_OF_DIRECTORS_ROLE_GROUP_ID, createStaffObject(formState, viewState, existingPersonOrgRoles));
      } else {
        const overlap = getOverlappingDates(viewState.personOrgRoleId, viewState.personOrgRoleDurationId, formState.effectiveDate, formState.expirationDate, existingPersonOrgRoles);
        setErrors({
          multipleActiveRoles: `This member has an existing ${viewState.orgRoleName} role from ${formatDate(overlap?.effectiveDate)} - ${formatDate(overlap?.expirationDate)}. Date overlaps are not allowed.`
        });
      }
    }
  };

  function getExistingPersonOrgRoles(personId) {
    const existingPerson = memberAdministrationFoundationBoardState.arrayData.find(x => x.personId === personId);

    if (existingPerson && Array.isArray(existingPerson.orgRoles)) {
      return existingPerson.orgRoles;
    } else {
      return null;
    }
  };

  function isDurationValid(personOrgRoleId, personOrgRoleDurationId, effectiveDate, expirationDate, orgRoles) {
    let overlappingDate = false;

    for (const role of orgRoles) {
      if (role.personOrgRoleId === personOrgRoleId && role.personOrgRoleDurationId !== personOrgRoleDurationId) {
        if (new Date(effectiveDate) <= new Date(role.expirationDate)
          && new Date(expirationDate) >= new Date(role.effectiveDate)) {
          overlappingDate = true;
          break;
        }
      }
    }

    return !overlappingDate;
  };

  function getOverlappingDates(personOrgRoleId, personOrgRoleDurationId, effectiveDate, expirationDate, orgRoles) {
    let overlappingDuration = {};

    for (const role of orgRoles) {
      if (role.personOrgRoleId === personOrgRoleId && role.personOrgRoleDurationId !== personOrgRoleDurationId) {
        if (new Date(effectiveDate) <= new Date(role.expirationDate)
          && new Date(expirationDate) >= new Date(role.effectiveDate)) {
            overlappingDuration = role;
          break;
        }
      }
    }

    return overlappingDuration;
  };

  function createStaffObject(formState, viewState, existingPersonOrgRoles) {
    const filteredPersonOrgRoles = [];

    if (Array.isArray(existingPersonOrgRoles)) {
      for (const role of existingPersonOrgRoles) {
        if (role.isCurrent === true && role.personOrgRoleDurationId !== viewState.personOrgRoleDurationId) {
          filteredPersonOrgRoles.push({
            orgRoleId: role.orgRoleId,
            orgRoleName: role.orgRoleName,
            personOrgRoleId: role.personOrgRoleId,
            personOrgRoleDurationId: role.personOrgRoleDurationId
          });
        }
      }
    }

    return {
      personId: viewState.personId,
      personOrgRole: [
        {
          orgRoleId: viewState.orgRoleId,
          orgRoleName: viewState.orgRoleName,
          expirationDate: formState.expirationDate,
          personOrgRoleId: viewState.personOrgRoleId,
          personOrgRoleDurationId: viewState.personOrgRoleDurationId
        },
        ...filteredPersonOrgRoles
      ]
    };
  };

  function getInitialFormState() {
    return {
      effectiveDate: Constants.BLANK_DATE_STRING,
      expirationDate: Constants.BLANK_DATE_STRING
    };
  };

  function getInitialViewState() {
    return {
      orgRoleName: '',
      orgRoleId: '',
      memberName: '',
      memberId: '',
      personId: '',
      personOrgRoleId: Constants.DEFAULT_ID,
      personOrgRoleDurationId: Constants.DEFAULT_ID
    };
  };

  return {
    isLoading: memberAdministrationFoundationBoardState.isArrayLoading || environmentVariableState.isLoading || isSubmitting,
    isSaving: memberAdministrationFoundationBoardState.isSaving,
    viewState,
    formState,
    errorState,
    onFormValueChanged,
    handleSubmit,
    onBackClicked,
    onSaveClicked
  };
};

export default useEditFoundationBoardMember;